import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckboxGroup,
  CheckboxGroupItem,
  TextArea,
} from '@spotted-zebra-uk/ui-components';
import {
  DeiDisabilityIdentify,
  DeiNeurodivergenceIdentify,
} from '../../generated/graphql';
import { otherFieldId } from './StageActionDeiForm.helpers';
import styles from './StageActionDeiForm.module.scss';

type DeiCheckboxFormFieldProps = {
  id: string;
  onChange: (value: string[]) => void;
  options: { label: string; value: string }[];
  value: string[];
  error: string;
  showOther: boolean;
  onOtherChange: (value: string, id: string) => void;
  textAreaValue: string;
  focusOtherField: boolean;
  textAreaError: string;
};

const OTHER_FIELD_VALUE = [
  DeiDisabilityIdentify.Other.toString(),
  DeiNeurodivergenceIdentify.Other.toString(),
];

const DeiCheckboxFormField = ({
  id,
  onChange,
  options,
  value,
  error,
  showOther,
  onOtherChange,
  textAreaValue,
  focusOtherField,
  textAreaError,
}: DeiCheckboxFormFieldProps) => {
  const { t } = useTranslation();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (focusOtherField && textAreaRef.current) {
      const field = textAreaRef.current;
      field.focus(); // Focus will place the caret at the beginning of a previous response if it exists
      field.setSelectionRange(field.value.length, field.value.length); // move caret to end
    }
  }, [focusOtherField]);

  return (
    <div className={styles.formFieldContainer}>
      <label>{t(`test.dei.${id}`)}</label>
      <p className={styles.selectAllThatApply}>
        ({t(`test.dei.selectAllThatApply`)})
      </p>
      <CheckboxGroup
        id={`${id}-checkbox-group`}
        value={value}
        onChange={onChange}
        hasError={!!error && !textAreaError}
        error={error}
      >
        {options.map((option, index) =>
          !OTHER_FIELD_VALUE?.includes(option.value) ? (
            <CheckboxGroupItem
              key={option.value}
              id={option.value}
              label={t(`test.dei.${option.label}`)}
            />
          ) : (
            <>
              <CheckboxGroupItem
                key={`${option.value}-${index}`}
                id={option.value}
                label={t(`test.dei.${option.label}`)}
              />
              {showOther && (
                <div
                  className={clsx(
                    styles.otherTextAreaContainer,
                    error && styles.withError
                  )}
                >
                  <TextArea
                    ref={textAreaRef}
                    id={otherFieldId(id)}
                    title={t(`common.textAreaTitleOther`)}
                    onChange={onOtherChange}
                    hasError={!!textAreaError}
                    value={textAreaValue}
                  />
                  {textAreaError && (
                    <p className={styles.error}>{textAreaError}</p>
                  )}
                </div>
              )}
            </>
          )
        )}
      </CheckboxGroup>
    </div>
  );
};

export default DeiCheckboxFormField;
